import { NgIf } from '@angular/common';
import {
    AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, OnChanges, OnDestroy,
    OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ICompany, RequestLicense, RequestUserAccessEmailSet } from '@myapp/features/admin/models';
import { AdminService } from '@myapp/features/admin/services';
import { AuthPermIdentifier, RequestUserPermission } from '@myapp/modules/fwcore/models';
import {
    AuthorizationService, ConfirmationDialogHtmlPaneService, ConfirmationDialogService,
    NotificationDialogService, NotificationPopupService
} from '@myapp/modules/fwcore/services';
import { ControlResizerOptions, ControlResizerService, IsgHelperService } from '@mysrc/services/framework';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { SelectEvent, TabStripModule } from '@progress/kendo-angular-layout';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TriStateCheckBoxComponent } from '../../../widgets/components/tri-state-check-box/tri-state-check-box.component';
import { UserImportComponent } from '../../components/users/user-import/user-import.component';
import { UserStateComponent } from '../../components/users/user-state/user-state.component';

@Component({
  selector: 'app-page-user-license',
  standalone: true,
  imports: [RouterLink, TabStripModule, TextBoxModule, FormsModule, TriStateCheckBoxComponent, ButtonModule
    , UserStateComponent, NgIf, UserImportComponent],
  templateUrl: './page-user-license.component.html',
  styleUrls: ['./page-user-license.component.scss']
})
export class PageUserLicenseComponent implements AfterViewChecked, OnInit, OnDestroy, AfterViewInit, OnChanges {

  /*-------------------------- Input / Outputs ------------------------*/


  /*-------------------------- public properties / methods ------------*/

  public currentLicenseId: string | null = null;

  public currentCompanyId: string | null = null;

  public userStartsWith: string = "";

  public userAssigned: boolean | null = true;

  public userActive: boolean | null = true;

  public companyName: string | undefined | null = null;

  /*-------------------------- Internal properties  -------------------*/

  protected companies: ICompany[] | undefined;

  protected destroy$ = new Subject();

  protected height!: number;

  protected viewInit: boolean = false;

  protected loading: boolean = false;


  protected hrefLicenses: string = "./admin/license";
  protected hrefUserManagement: string = "./admin/user/license";
  protected hrefLicenseRoles: string = "./admin/role";
  protected hrefUserPermission: string = "./admin/user/permission";

  @ViewChild(UserStateComponent)
  protected componentUserState!: UserStateComponent;

  @ViewChild(UserImportComponent)
  protected componentUserImport!: UserImportComponent;

  /*-------------------------- State  --------------------------*/

  protected reqPerm: RequestUserPermission = {
  }

  protected perms?: AuthPermIdentifier[] = [];


  protected hasUserImport: boolean = false;

  /*--------------------------- Constructor  --------------------------*/

  constructor(protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected dialogConfirmPane: ConfirmationDialogHtmlPaneService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected controlResizer: ControlResizerService
    , protected cdr: ChangeDetectorRef
    , protected hlp: IsgHelperService
  ) {

  }

  /*--------------------------- ng  -----------------------------------*/

  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {
          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) { this.router.navigate(['./admin/license']); }

          this.updateCompanyInfo();

          this.hrefLicenses = `./admin/license?id=` + this.currentLicenseId;
          this.hrefUserManagement = "./admin/user/license?id=" + this.currentLicenseId;
          this.hrefLicenseRoles = `./admin/role?id=` + this.currentLicenseId;
          this.hrefUserPermission = `./admin/user/permission?id=` + this.currentLicenseId;
        }
      );

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );

    this.reqPerm.hierarchyCodes = ['users'];

    this.serviceAuth.pagePermissionsGet$(this.reqPerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.perms = resp.data ?? [];

        this.hasUserImport = this.perms?.some(i => i.code === "user-import") ?? false;
      });

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewChecked(): void {

    this.sizeChange();
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {

  }

  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {


  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.sizeChange();
  }

  onTabSelect(e: SelectEvent): void {
    this.sizeChange();
  }

  onSendEmails(e: Event): void {

    var userIds = this.componentUserState.checkedIds;

    if (!userIds || userIds.length < 1) {
      this.dialogNotification.openNotificationDialog("Send Access Emails", "Select users to send access emails to by ticking their checkboxes");
      return;
    }

    var msg = "Do you wish to proceed and send out access emails to users?";

    this.dialogConfirmPane.open('Send Access Emails', msg, 300)
      .subscribe((result: any) => {
        if (result.value) {

          const req: RequestUserAccessEmailSet = {
            licenseId: this.currentLicenseId
            , data: userIds
          };

          this.sendAccessEmails(req);
        }
      });
  }

  /*--------------------------- internal functions  -------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 94;
      options.offsetBottomHeight = 21;

      this.height = this.controlResizer.calculateHeight(options);

      options.offsetTopHeight = 6;
      options.offsetBottomHeight = 29;
      this.sizeChangeSiblings(options);
    }

  }

  protected sizeChangeSiblings(options: ControlResizerOptions) {

    if (this.componentUserState)
      this.componentUserState.heightChange(options);

    if (this.componentUserImport)
      this.componentUserImport.heightChange(options);

  }

  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {
        if (resp.data && resp.data.length === 1) {
          this.companyName = resp.data[0].companyName;
          this.currentCompanyId = (resp.data[0].companyId) ? resp.data[0].companyId : null;
        }
        else {
          this.companyName = "";
          this.currentCompanyId = null;
        }
      }
    );
  }

  protected sendAccessEmails(req: RequestUserAccessEmailSet) {

    this.loading = true;

    this.adminService.setUserAccessEmail$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success) {
          let msg = `${res.emailTotal} processed ${res.emailSuccess} sent, ${res.emailFailure} failure(s). ${res.message}`;
          this.dialogNotification.openNotificationDialog('Send Access Emails', msg);
        }
        else {
          let msg = `${res.emailTotal} processed ${res.emailSuccess} sent, ${res.emailFailure} failure(s).\n ${res.message}`;
          this.dialogNotification.openNotificationDialog('Send Access Emails', msg);
        }
      });

  }

}
